export type PlanRow = {
  code: string;
  is_active: boolean;
  paid_orders: number;
};

export type PetRow = {
  id: number;
  name: string;
  plans: PlanRow[];
};

export type SessionRow = {
  session_started_at: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  referrer: string | null;
  gclid: string | null;
  pets: PetRow[];
};

export type SessionsList = {
  count: number;
  page: number;
  page_size: number;
  results: SessionRow[];
};

// Plan state -> how many of the stage's plans sit there today. Sparse: the
// backend only sends buckets with a count, so always read through a default.
export type StatusCounts = Record<string, number>;

// ko_reason code -> count. '' groups the plans with no reason recorded.
export type ReasonCounts = Record<string, number>;

// Quién vendió cada conversión (FFJDS-2203) -> cuántas. 'unassigned' agrupa
// las que la clasificación nocturna aún no ha visto.
export type SalesAttributionCounts = Record<string, number>;

// Por dónde llegó cada pre-lead (FFJDS-2574): 'call-request' o
// 'vixiees-inbound' -> cuántos.
export type LeadSourceCounts = Record<string, number>;

export const FUNNEL_STAGES = [
  'sessions',
  'leads',
  'trials',
  'subscriptions',
] as const;

export type FunnelStage = (typeof FUNNEL_STAGES)[number];

export type StageCounts = Record<FunnelStage, number>;

// Cifras del embudo filtrado por un canal. null cuando su petición falló.
export type ChannelStageCounts = {
  name: string;
  counts: StageCounts | null;
};

export type ChannelMixRow = { name: string; counts: StageCounts };

// Reparto de las etapas del embudo entre los canales llegados. `remainder` es
// lo que no reclama ninguno: mientras faltan canales, lo aún sin repartir; al
// acabar, Otro: lo que no reclama ningún otro canal.
export type ChannelMix = {
  channels: ChannelMixRow[];
  remainder: StageCounts;
  failedNames: string[];
  stageTotals: StageCounts;
};

const EMPTY_STAGE_COUNTS: StageCounts = {
  sessions: 0,
  leads: 0,
  trials: 0,
  subscriptions: 0,
};

function sumStageCounts(list: StageCounts[]): StageCounts {
  const sum = { ...EMPTY_STAGE_COUNTS };
  for (const counts of list) {
    for (const stage of FUNNEL_STAGES) sum[stage] += counts[stage];
  }
  return sum;
}

export function computeChannelMix(
  arrived: ChannelStageCounts[],
  total: StageCounts
): ChannelMix {
  const channels = arrived.filter((c): c is ChannelMixRow => c.counts !== null);
  const failedNames = arrived
    .filter((c) => c.counts === null)
    .map((c) => c.name);
  // Los canales se cuentan por separado: si se solapan, la suma puede pasar
  // del total y entonces no queda nada sin repartir.
  const channelsSum = sumStageCounts(channels.map((c) => c.counts));
  const remainder = { ...EMPTY_STAGE_COUNTS };
  for (const stage of FUNNEL_STAGES) {
    remainder[stage] = Math.max(0, total[stage] - channelsSum[stage]);
  }
  return {
    channels,
    remainder,
    failedNames,
    // Cada etapa suma el 100 % de lo repartido en ella.
    stageTotals: sumStageCounts([channelsSum, remainder]),
  };
}

export function channelMixShare(
  mix: ChannelMix,
  count: number,
  stage: FunnelStage
): number {
  const stageTotal = mix.stageTotals[stage];
  return stageTotal > 0 ? count / stageTotal : 0;
}

export type FunnelTotals = {
  sessions: number;
  pre_leads?: number;
  leads: number;
  trials: number;
  subscriptions: number;
  pauses?: number;
  pauses_by_reason?: ReasonCounts;
  pre_leads_source?: LeadSourceCounts;
  leads_status?: StatusCounts;
  trials_status?: StatusCounts;
  trials_attribution?: SalesAttributionCounts;
  subscriptions_status?: StatusCounts;
  sessions_list?: SessionsList;
};

// Plan.STATE_CHOICES order, so columns read as a lifecycle instead of
// alphabetically. Anything the backend adds later lands at the end.
const PLAN_STATE_ORDER = [
  'proposal',
  'active',
  'reactivating',
  'payment-failed',
  'no-payment-method',
  'detained',
  'paused',
  'cancelled',
  'discarded',
  'obsolete',
];

// Estados de una etapa en orden de ciclo de vida. Se omiten los que están a
// cero: como sub-filas no aportan nada, al contrario que cuando eran columnas
// y hacía falta el hueco para alinear.
export function sortedStatuses(
  status: StatusCounts | null
): Array<[string, number]> {
  if (!status) return [];
  return Object.entries(status)
    .filter(([, count]) => count > 0)
    .sort(([a], [b]) => {
      const indexA = PLAN_STATE_ORDER.indexOf(a);
      const indexB = PLAN_STATE_ORDER.indexOf(b);
      if (indexA === -1 && indexB === -1) return a.localeCompare(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
}

// De más a menos asistida; las sin clasificar al final.
const SALES_ATTRIBUTION_ORDER = ['agent', 'atc', 'direct', 'unassigned'];

const LEAD_SOURCE_ORDER = ['call-request', 'vixiees-inbound'];

// Entradas en un orden fijo, sin las que están a cero.
function inFixedOrder(
  order: string[],
  counts: Record<string, number>
): Array<[string, number]> {
  return order
    .map((key): [string, number] => [key, counts[key] ?? 0])
    .filter(([, count]) => count > 0);
}

// Reasons sorted by count desc so the dominant ones read first; '' (no reason
// recorded) is pushed last regardless of how big it is.
export function sortedReasons(reasons: ReasonCounts): Array<[string, number]> {
  return Object.entries(reasons)
    .filter(([, count]) => count > 0)
    .sort(([codeA, a], [codeB, b]) => {
      if (codeA === '') return 1;
      if (codeB === '') return -1;
      return b - a;
    });
}

export type MetricId =
  | 'sessions'
  | 'preLeads'
  | 'leads'
  | 'trials'
  | 'subscriptions'
  | 'pauses';

// Las pausas no son un paso del embudo: se pintan en su propia tabla, debajo
// de las etapas.
const CHURN_METRICS: MetricId[] = ['pauses'];

export function isChurnMetric(id: MetricId): boolean {
  return CHURN_METRICS.includes(id);
}

// Cómo etiquetar cada entrada del desglose: los estados de plan, el reparto de
// ventas y el origen de los pre-leads tienen traducción, los códigos de baja
// se pintan en crudo.
export type BreakdownKind =
  | 'planState'
  | 'reason'
  | 'salesAttribution'
  | 'leadSource';

// Entradas ya ordenadas y sin ceros.
export type Breakdown = {
  kind: BreakdownKind;
  entries: Array<[string, number]>;
};

export type MetricRow = {
  id: MetricId;
  count: number;
  // Conversión respecto a la métrica anterior. null en las que no encadenan:
  // Sesiones abre el embudo, y las bajas no son un paso de él.
  stepConversion: number | null;
  // Solo los que tienen entradas. Vacío significa que la fila no despliega.
  breakdowns: Breakdown[];
};

function nonEmpty(breakdowns: Breakdown[]): Breakdown[] {
  return breakdowns.filter(({ entries }) => entries.length > 0);
}

// Conversión respecto a la métrica anterior. null si la anterior está a cero.
export function conversionRate(
  count: number,
  previousCount: number
): number | null {
  return previousCount > 0 ? count / previousCount : null;
}

// Conversión de una etapa desde la anterior con las mismas cifras (p. ej. las
// de un canal). null en Sesiones, que abre el embudo.
export function stageConversion(
  counts: StageCounts,
  stage: FunnelStage
): number | null {
  const index = FUNNEL_STAGES.indexOf(stage);
  if (index === 0) return null;
  return conversionRate(counts[stage], counts[FUNNEL_STAGES[index - 1]]);
}

export function computeMetricRows(t: FunnelTotals): MetricRow[] {
  const planStates = (status: StatusCounts | undefined): Breakdown => ({
    kind: 'planState',
    entries: sortedStatuses(status ?? null),
  });
  const stage = (
    id: MetricId,
    count: number,
    stepConversion: number | null,
    breakdowns: Breakdown[]
  ): MetricRow => ({
    id,
    count,
    stepConversion,
    breakdowns: nonEmpty(breakdowns),
  });
  // Las bajas no llevan porcentaje: no son un paso del embudo y cualquier
  // denominador induce a leerlas como tasa de cohorte, que no lo son. El
  // backend las cuenta por paused_at sin mirar cuándo se cobró el plan, así
  // que incluyen planes captados antes del rango.
  const churn = (
    id: MetricId,
    count: number,
    reasons: ReasonCounts | undefined
  ): MetricRow => ({
    id,
    count,
    stepConversion: null,
    breakdowns: nonEmpty([
      { kind: 'reason', entries: sortedReasons(reasons ?? {}) },
    ]),
  });

  return [
    stage('sessions', t.sessions, null, []),
    // Sin conversión: los leads no salen solo de pre-leads (la mayoría crea
    // el plan sin contactar antes), así que Leads la sigue midiendo respecto a
    // Sesiones.
    stage('preLeads', t.pre_leads ?? 0, null, [
      {
        kind: 'leadSource',
        entries: inFixedOrder(LEAD_SOURCE_ORDER, t.pre_leads_source ?? {}),
      },
    ]),
    stage('leads', t.leads, conversionRate(t.leads, t.sessions), [
      planStates(t.leads_status),
    ]),
    // Trials se reparte además por quién hizo la venta, antes que por estado.
    stage('trials', t.trials, conversionRate(t.trials, t.leads), [
      {
        kind: 'salesAttribution',
        entries: inFixedOrder(
          SALES_ATTRIBUTION_ORDER,
          t.trials_attribution ?? {}
        ),
      },
      planStates(t.trials_status),
    ]),
    stage(
      'subscriptions',
      t.subscriptions,
      conversionRate(t.subscriptions, t.trials),
      [planStates(t.subscriptions_status)]
    ),
    churn('pauses', t.pauses ?? 0, t.pauses_by_reason),
  ];
}

export function formatPercent(x: number | null): string {
  if (x === null) return '';
  // Tolerate up to 2 decimals while keeping at least 1 (drop a single trailing
  // zero, so 100.00 → 100.0, 12.30 → 12.3, but 33.33 / 0.05 stay).
  return `${(x * 100).toFixed(2).replace(/0$/, '')}%`;
}
